import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Heart, Search, Plus, User, Shield, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { AuthPromptModal } from '@/components/auth/AuthPromptModal';
import { useMapStore } from '@/store/mapStore';
import { toast } from 'sonner';
import jewmapLogo from '@/assets/jewmap-logo.png';

type NavItem = 'favorites' | 'locate' | 'map' | 'add' | 'profile' | 'admin';

interface BottomNavProps {
  activeItem: NavItem;
  onItemClick: (item: NavItem) => void;
  onSearchClick?: () => void;
}

export const BottomNav = ({ activeItem, onItemClick, onSearchClick }: BottomNavProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdminOrMod } = useUserRole();
  const { 
    isLocating, 
    setUserLocation, 
    setIsLocating, 
    setLocationError,
    setCenter 
  } = useMapStore();
  const [authModal, setAuthModal] = useState<{ isOpen: boolean; action: 'favorites' | 'add' }>({
    isOpen: false,
    action: 'favorites',
  });
  
  // Animation states
  const [tappedItem, setTappedItem] = useState<NavItem | null>(null);
  const [bouncingItem, setBouncingItem] = useState<NavItem | null>(null);
  const rippleRef = useRef<HTMLDivElement>(null);

  const handleGeolocate = () => {
    if (!navigator.geolocation) {
      toast.error('La géolocalisation n\'est pas supportée par votre navigateur');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('[BottomNav] Position received:', latitude, longitude);
        
        setUserLocation([longitude, latitude]);
        setCenter([longitude, latitude]);
        
        setTimeout(() => {
          console.log('[BottomNav] Invalidating places queries');
          queryClient.invalidateQueries({ queryKey: ['places'] });
          setIsLocating(false);
          toast.success('Position trouvée');
        }, 100);
      },
      (error) => {
        setIsLocating(false);
        let message = 'Impossible d\'obtenir votre position';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Permission de localisation refusée';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Position indisponible';
            break;
          case error.TIMEOUT:
            message = 'Délai d\'attente dépassé';
            break;
        }
        
        setLocationError(message);
        toast.error(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  // Trigger bounce animation
  const triggerBounce = useCallback((itemId: NavItem) => {
    setBouncingItem(itemId);
    setTimeout(() => setBouncingItem(null), 400);
  }, []);

  // Handle tap with press effect
  const handleTouchStart = useCallback((itemId: NavItem) => {
    setTappedItem(itemId);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setTimeout(() => setTappedItem(null), 100);
  }, []);

  const navItems: { id: NavItem; label: string; icon: React.ReactNode; adminOnly?: boolean }[] = [
    { id: 'favorites', label: 'Favoris', icon: <Heart className="w-5 h-5" /> },
    { id: 'locate', label: 'Rechercher', icon: isLocating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" /> },
    { id: 'map', label: 'Carte', icon: null },
    { id: 'add', label: 'Ajouter', icon: <Plus className="w-5 h-5" /> },
    { id: 'admin', label: 'Admin', icon: <Shield className="w-5 h-5" />, adminOnly: true },
    { id: 'profile', label: 'Profil', icon: <User className="w-5 h-5" /> },
  ];

  const visibleNavItems = navItems.filter(item => !item.adminOnly || isAdminOrMod);

  const handleNavClick = (item: NavItem) => {
    // Trigger bounce animation on tap
    triggerBounce(item);
    
    if (item === 'favorites') {
      if (user) {
        navigate('/favorites');
      } else {
        setAuthModal({ isOpen: true, action: 'favorites' });
      }
    } else if (item === 'add') {
      if (user) {
        navigate('/add-place');
      } else {
        setAuthModal({ isOpen: true, action: 'add' });
      }
    } else if (item === 'profile') {
      if (user) {
        navigate('/profile');
      } else {
        navigate('/auth');
      }
    } else if (item === 'locate') {
      onSearchClick?.();
    } else if (item === 'admin') {
      navigate('/admin');
    } else if (item === 'map') {
      handleGeolocate();
      onItemClick(item);
    } else {
      onItemClick(item);
    }
  };

  // Calculate pill position for active indicator
  const getActiveIndex = () => {
    return visibleNavItems.findIndex(item => item.id === activeItem);
  };

  return (
    <>
      <nav 
        className="border-t border-border/30 relative"
        style={{ height: '72px' }}
      >
        {/* Animated pill indicator */}
        <div 
          className="absolute top-0 h-0.5 bg-primary rounded-full transition-all duration-300 ease-out animate-pill-slide"
          style={{
            width: `${100 / visibleNavItems.length}%`,
            left: `${(getActiveIndex() / visibleNavItems.length) * 100}%`,
          }}
        />
        
        <div className="flex items-center justify-around px-2 py-2">
          {visibleNavItems.map((item) => (
            <button
              key={item.id}
              data-tour-id={
                item.id === 'map' ? 'nav-map' :
                item.id === 'locate' ? 'nav-search' :
                item.id === 'favorites' ? 'nav-favorites' :
                item.id === 'add' ? 'nav-add' :
                item.id === 'profile' ? 'nav-profile' :
                item.id === 'admin' ? 'nav-admin' : undefined
              }
              onClick={() => handleNavClick(item.id)}
              onTouchStart={() => handleTouchStart(item.id)}
              onTouchEnd={handleTouchEnd}
              onMouseDown={() => handleTouchStart(item.id)}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              className={cn(
                'nav-item flex-1 relative transition-all duration-200',
                activeItem === item.id && 'active',
                tappedItem === item.id && 'scale-90',
              )}
            >
              {item.id === 'map' ? (
                // Center logo button with glow effect
                <div className={cn(
                  'relative -mt-8 rounded-full transition-all duration-300 overflow-hidden',
                  'bg-card border-4 border-background',
                  'w-[72px] h-[72px] flex items-center justify-center',
                  'shadow-[0_4px_16px_rgba(0,0,0,0.2)]',
                  activeItem === 'map' && 'shadow-glow scale-105',
                  tappedItem === 'map' && 'scale-95',
                  bouncingItem === 'map' && 'animate-icon-bounce'
                )}>
                  {/* Pulsing ring behind logo */}
                  <div className={cn(
                    'absolute inset-0 rounded-full transition-opacity duration-500',
                    activeItem === 'map' && 'animate-glow-pulse'
                  )} />
                  
                  {isLocating ? (
                    <Loader2 className="w-10 h-10 animate-spin text-primary" />
                  ) : (
                    <img 
                      src={jewmapLogo} 
                      alt="JewMap"
                      width={48}
                      height={48}
                      className={cn(
                        "w-12 h-12 object-contain transition-transform duration-300",
                        tappedItem === 'map' && 'scale-90'
                      )}
                    />
                  )}
                </div>
              ) : (
                <>
                  <span className={cn(
                    'transition-all duration-200',
                    activeItem === item.id ? 'text-primary' : 'text-muted-foreground',
                    bouncingItem === item.id && 'animate-icon-bounce'
                  )}>
                    {item.icon}
                  </span>
                  <span className={cn(
                    'text-xs transition-all duration-200',
                    activeItem === item.id ? 'text-primary font-medium' : 'text-muted-foreground'
                  )}>
                    {item.label}
                  </span>
                  
                  {/* Active glow dot */}
                  {activeItem === item.id && (
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary animate-glow-pulse" />
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      </nav>

      <AuthPromptModal
        isOpen={authModal.isOpen}
        onClose={() => setAuthModal({ ...authModal, isOpen: false })}
        action={authModal.action}
      />
    </>
  );
};
